import Category from '../models/Category';
import Group from '../models/Group';
import Chart from '../models/Chart';
import Fabric from '../models/Fabric';
import Code from '../models/Code';
import Package from '../models/Package';
import SetupOpt from '../models/SetupOpt';
import StripeComplete from '../models/StripeComplete';
import LetPriceCode from '../models/LetPriceCode';
import Company from '../models/Company';
import MinGroup from '../models/MinGroup';
import Image from '../models/Image';
import CustomLtrSetup from '../models/CustomLtrSetup';
import apiClient from '../components/apiClient';

class MaintenanceService {
    private static formatDate(dateTimeString: string): string {
        const date = new Date(dateTimeString);
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    }

    private static getFormattedData<T extends Record<string, any>>(data: T[], dateKeys: (keyof T & string)[]): T[] {
        return data.map(item => {
            dateKeys.forEach(key => {
                if (item[key] && typeof item[key] === 'string') {
                    item[key] = this.formatDate(item[key] as string) as any;
                }
            });
            return item;
        });
    }

    private static fetchData<T extends Record<string, any>>(url: string, dateKeys: (keyof T & string)[] = []): Promise<T[]> {
        return apiClient.get<T[]>(url).then(res => this.getFormattedData(res.data, dateKeys));
    }

    private static postData<T>(url: string, payload: T): Promise<T> {
        return apiClient.post<T>(url, payload).then(res => res.data);
    }

    private static putData<T>(url: string, payload: T): Promise<T> {
        return apiClient.put<T>(url, payload).then(res => res.data);
    }

    private static deleteData(url: string): Promise<boolean> {
        return apiClient.delete<boolean>(url).then(res => res.data);
    }

    // Charts Endpoints
    static getCharts(): Promise<Chart[]> {
        return this.fetchData<Chart>(`/Maintenance/Charts`, ['chartUpdTs']);
    }

    static getChartsByType(p: string): Promise<Chart[]> {
        return this.fetchData<Chart>(`/Maintenance/Charts/ByType/${p}`, ['chartUpdTs']);
    }

    static addChart(p: Chart): Promise<Chart> {
        return this.postData<Chart>(`/Maintenance/Charts`, p);
    }

    static updateChart(p: Chart, oldId: string): Promise<Chart> {
        return this.putData<Chart>(`/Maintenance/Charts/${oldId}`, p);
    }

    static deleteChart(p: string): Promise<boolean> {
        return this.deleteData(`/Maintenance/Charts/${p}`);
    }

    // Categories Endpoints
    static getCategories(): Promise<Category[]> {
        return this.fetchData<Category>(`/Maintenance/Categories`, ['categoryUpdTs']);
    }

    static addCategory(p: Category): Promise<Category> {
        return this.postData<Category>(`/Maintenance/Categories`, p);
    }

    static updateCategory(p: Category, oldId: string): Promise<Category> {
        return this.putData<Category>(`/Maintenance/Categories/${oldId}`, p);
    }

    static deleteCategory(p: string): Promise<boolean> {
        return this.deleteData(`/Maintenance/Categories/${p}`);
    }

    // Groups Endpoints
    static getGroups(): Promise<Group[]> {
        return this.fetchData<Group>(`/Maintenance/Groups`, ['groupUpdTs']);
    }

    static addGroup(p: Group): Promise<Group> {
        return this.postData<Group>(`/Maintenance/Groups`, p);
    }

    static updateGroup(p: Group, oldId: string): Promise<Group> {
        return this.putData<Group>(`/Maintenance/Groups/${oldId}`, p);
    }

    static deleteGroup(p: string): Promise<boolean> {
        return this.deleteData(`/Maintenance/Groups/${p}`);
    }

    // SetupOpt Endpoints
    static getSetupOptions(): Promise<SetupOpt[]> {
        return this.fetchData<SetupOpt>(`/Maintenance/SetupOptions`, ['setupOptUpdTs']);
    }

    static addSetupOption(p: SetupOpt): Promise<SetupOpt> {
        return this.postData<SetupOpt>(`/Maintenance/SetupOptions`, p);
    }

    static updateSetupOption(p: SetupOpt): Promise<SetupOpt> {
        return this.putData<SetupOpt>(`/Maintenance/SetupOptions/${p.setupOptId}`, p);
    }

    static deleteSetupOption(p: number): Promise<boolean> {
        return this.deleteData(`/Maintenance/SetupOptions/${p}`);
    }

    // Stripes Endpoints
    static getStripes(): Promise<StripeComplete[]> {
        return this.fetchData<StripeComplete>(`/Maintenance/Stripes`, ['stripeUpdTs']);
    }

    static addStripe(p: StripeComplete): Promise<StripeComplete> {
        return this.postData<StripeComplete>(`/Maintenance/Stripes`, p);
    }

    static updateStripe(p: StripeComplete, oldId: string): Promise<StripeComplete> {
        return this.putData<StripeComplete>(`/Maintenance/Stripes/${oldId}`, p);
    }

    static deleteStripe(p: string): Promise<boolean> {
        return this.deleteData(`/Maintenance/Stripes/${p}`);
    }


    // LetPriceCd Endpoints
    static getLetPriceCds(): Promise<LetPriceCode[]> {
        return this.fetchData<LetPriceCode>(`/Maintenance/LetPriceCds`, ['letPriceCdUpdTs']);
    }

    static addLetPriceCd(p: LetPriceCode): Promise<LetPriceCode> {
        return this.postData<LetPriceCode>(`/Maintenance/LetPriceCds`, p);
    }

    static updateLetPriceCd(p: LetPriceCode, oldId: string): Promise<LetPriceCode> {
        return this.putData<LetPriceCode>(`/Maintenance/LetPriceCds/${oldId}`, p);
    }

    static deleteLetPriceCd(p: string): Promise<boolean> {
        return this.deleteData(`/Maintenance/LetPriceCds/${p}`);
    }

    // Min Groups Endpoints
    static getMinGroups(): Promise<MinGroup[]> {
        return this.fetchData<MinGroup>(`/Maintenance/MinGroups`, ['minGroupUpdTs']);
    }

    static addMinGroup(p: MinGroup): Promise<MinGroup> {
        return this.postData<MinGroup>(`/Maintenance/MinGroups`, p);
    }

    static updateMinGroup(p: MinGroup): Promise<MinGroup> {
        return this.putData<MinGroup>(`/Maintenance/MinGroups/${p.minGroupId}`, p);
    }

    static deleteMinGroup(p: number): Promise<boolean> {
        return this.deleteData(`/Maintenance/MinGroups/${p}`);
    }

    // Drawings Endpoints
    static getDrawings(): Promise<Image[]> {
        return this.fetchData<Image>(`/Maintenance/Images`, ['imageUpdTs']);
    }

    static getDrawingsById(p: Image): Promise<Image> {
        return apiClient.get<Image>(`/Maintenance/Images/${p.imageId}`).then(res => res.data);
    }

    static addDrawing(p: Image): Promise<Image> {
        return this.postData<Image>(`/Maintenance/Images`, p);
    }

    static updateDrawing(p: Image): Promise<Image> {
        return this.putData<Image>(`/Maintenance/Images/${p.imageId}`, p);
    }

    static deleteDrawing(p: number): Promise<boolean> {
        return this.deleteData(`/Maintenance/Images/${p}`);
    }
    
    // Fabric Endpoints
    static getFabrics(): Promise<Fabric[]> {
        return this.fetchData<Fabric>(`/Maintenance/Fabrics`, ['fabricUpdTs']);
    }

    static getActiveFabrics(): Promise<Fabric[]> {
        return this.fetchData<Fabric>(`/Maintenance/Fabrics/${'A'}`, ['fabricUpdTs']);
    }

    static addFabric(fabric: Fabric): Promise<Fabric> {
        return this.postData<Fabric>(`/Maintenance/Fabrics`, fabric);
    }

    static updateFabric(fabric: Fabric, oldId: string): Promise<Fabric> {
        return this.putData<Fabric>(`/Maintenance/Fabrics/${oldId}`, fabric);
    }

    static deleteFabric(fabricCd: string): Promise<boolean> {
        return this.deleteData(`/Maintenance/Fabrics/${fabricCd}`);
    }

    static getCustomLtrSetups(): Promise<CustomLtrSetup[]> {
        return this.fetchData<CustomLtrSetup>(`/Maintenance/CustomLtrSetups`, []);
    }

    static addCustomLtrSetup(p: CustomLtrSetup): Promise<CustomLtrSetup> {
        return this.postData<CustomLtrSetup>(`/Maintenance/CustomLtrSetups`, p);
    }

    static updateCustomLtrSetup(p: CustomLtrSetup): Promise<CustomLtrSetup> {
        return this.putData<CustomLtrSetup>(`/Maintenance/CustomLtrSetups/${p.rowId}`, p);
    }

    static deleteCustomLtrSetup(p: number): Promise<boolean> {
        return this.deleteData(`/Maintenance/CustomLtrSetups/${p}`);
    }

    // Additional Endpoints
    static getSetupTypes(): Promise<Code[]> {
        return apiClient.get<Code[]>(`/Maintenance/Codes`, { params: { codeCategory: 'SETUP' } }).then(res => res.data);
    }

    static getSetupGroups(): Promise<Code[]> {
        return apiClient.get<Code[]>(`/Maintenance/Codes`, { params: { codeType: 'OPT' } }).then(res => res.data);
    }

    static getPackages(): Promise<Package[]> {
        return this.fetchData<Package>(`/Maintenance/Packages`, ['packageUpdTs', 'packageEffdt', 'packageDiscdt']);
    }

    static getCompanies(): Promise<Company[]> {
        return this.fetchData<Company>(`/Maintenance/Companies`, []);
    }
}

export default MaintenanceService;
